import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnInit,
    ChangeDetectionStrategy,
    inject,
    input,
    output,
} from '@angular/core';
import {
    FormsModule,
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { KeyValuePipe, KeyValue } from '@angular/common';
import { LabelModule } from '@progress/kendo-angular-label';
import { ComboBoxModule } from '@progress/kendo-angular-dropdowns';
import { TimePeriod, TimePeriodsMap } from '../../constants/time-periods';
import { SingleSelectMenuComponent } from '../single-select-menu/single-select-menu.component';

@Component({
    selector: 'app-time-period-aggregation-filter',
    imports: [
        FormsModule,
        LabelModule,
        ComboBoxModule,
        SingleSelectMenuComponent,
        KeyValuePipe,
    ],
    templateUrl: './time-period-aggregation-filter.component.html',
    styleUrls: ['./time-period-aggregation-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TimePeriodAggregationFilterComponent),
            multi: true,
        },
    ],
})
export class TimePeriodAggregationFilterComponent
    implements ControlValueAccessor, OnInit
{
    private cdr = inject(ChangeDetectorRef);

    readonly displayAsDropDown = input<boolean>(false);

    @Input()
    set timePeriods(value: { [key: string]: string }) {
        this._timePeriods = value ?? {};
        this.recomputeDisplayItems();
    }

    get timePeriods(): { [key: string]: string } {
        return this._timePeriods;
    }

    @Input() selectedValue: string = TimePeriod.Yearly;
    readonly selectedValueChange = output<string>();

    readonly preserveOrder = input<
        (a: KeyValue<string, string>, b: KeyValue<string, string>) => number
    >(() => 0);

    @Input()
    set reverseOrder(value: boolean) {
        this._reverseOrder = value;
        this.recomputeDisplayItems();
    }

    get displayItems(): { [key: string]: string } {
        return this._displayItems;
    }

    // internal model used by the template
    get internalSelectedValue(): string {
        return this.selectedValue;
    }

    set internalSelectedValue(value: string) {
        if (value !== this.selectedValue) {
            this.selectedValue = value;
            this.selectedValueChange.emit(value);
            this.onChange(value);
        }
    }

    protected isDisabled: boolean = false;

    private _displayItems: { [key: string]: string } = { ...TimePeriodsMap };
    private _reverseOrder: boolean = false;
    private _timePeriods: { [key: string]: string } = TimePeriodsMap;

    // ControlValueAccessor callbacks
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: string): void {
        this.selectedValue = value ?? TimePeriod.Yearly;
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    handleBlur(): void {
        this.onTouched();
    }

    ngOnInit(): void {
        this.recomputeDisplayItems();
    }

    private recomputeDisplayItems(): void {
        const entries = Object.entries(this._timePeriods);
        this._displayItems = Object.fromEntries(
            this._reverseOrder ? [...entries].reverse() : entries,
        );
    }
}
